import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Userpilot } from 'userpilot'
import { MedChronEmptyIllustration } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import AssetEmptyState from '@/matter-asset-common/asset/AssetEmptyState'
import { selectAddFilesStagingCountsForMatter, selectInitializeError } from '../store/selectors'
import { MedChronAssetActions } from '../store/slice'

interface MedChronAssetEmptyStateProps {
  matterId: string
}

/**
 * Blank state shown when the matter has no chronology yet (Figma: chronology/blank state). Thin
 * wrapper over the shared AssetEmptyState that supplies the med-chron copy, illustration, CTA, and
 * the first-generation-error banner (shown after a failed Generate).
 */
export default function MedChronAssetEmptyState(props: MedChronAssetEmptyStateProps) {
  const { matterId } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const stagingCounts = useAppSelector((state: RootState) => selectAddFilesStagingCountsForMatter(state, { matterId }))
  const stagingTotal = stagingCounts.pendingCount + stagingCounts.stagedCount

  // Re-run Userpilot once the empty-state lander has mounted so the feature-launch
  // announcement can match this page. The global route-change reload (routes/root.tsx)
  // fires on navigation, before this lander finishes its async asset load and paints.
  useEffect(() => {
    Userpilot.reload()
  }, [])

  return (
    <AssetEmptyState
      illustration={<MedChronEmptyIllustration className="h-10 w-[49px] shrink-0" />}
      title="Build your medical chronology"
      body="Build a complete timeline of visits, diagnoses, and procedures from your medical records, with every event cited to its source."
      ctaLabel="Add Files"
      onCtaClick={() => navigate(matterPaths.chronology.addFiles(matterId))}
      notice={
        stagingTotal > 0
          ? `${stagingTotal} ${stagingTotal === 1 ? 'file' : 'files'} ${stagingCounts.pendingCount > 0 ? 'uploading' : 'ready'} — continue in Add Files`
          : undefined
      }
      error={
        initializeError
          ? {
              headline: 'Chronology could not be generated.',
              detail: 'Please try again.',
              dismissLabel: 'Dismiss',
              onDismiss: () => dispatch(MedChronAssetActions.setInitializeError({ matterId, error: null })),
            }
          : undefined
      }
    />
  )
}
